/**
 * How an absence type's catalogue columns read on screen (Innstillinger → Fraværstyper and the
 * absence drawer). The values come from the API (backend/services/absenceCatalogue.js); the law
 * notes are the statutory facts behind a built-in key that no column holds.
 */
import type { ApiAbsenceType } from '@/api/data';
import { T } from '@/i18n';

export const PAYER: Record<NonNullable<ApiAbsenceType['payer']>, string> = {
  holiday_pay: 'Ulønnet, fra saldo',
  employer: 'Betalt arbeidsgiver',
  nav: 'Betalt NAV',
  employer_then_nav: 'Betalt arbeidsgiver, så NAV',
  unpaid: 'Ulønnet',
};

/** The payer when the type names one; otherwise what the pay file does with it. PAYER stays Norwegian (the keys); T() at the read. */
export const payerText = (t: ApiAbsenceType) =>
  T(t.payer ? PAYER[t.payer] : t.is_paid ? 'Betalt arbeidsgiver' : 'Ulønnet');

/** A type that draws on the vacation balance is counted as the company counts vacation (`vacationBasis`). */
export const unitText = (t: ApiAbsenceType, vacationBasis?: string) =>
  [
    T(t.type === 'time' ? 'Del av dag' : t.type === 'datetime' ? 'Fra–til' : 'Hel dag'),
    // the day basis is the API's Norwegian word (virkedager, kalenderdager, arbeidsdager)
    T((t.is_deductible && vacationBasis) || t.day_basis || ''),
  ]
    .filter(Boolean)
    .join(' · ');

/** Which balance the type draws on. Only feriedager is stored; the other two are counted. */
export const balanceText = (t: ApiAbsenceType) =>
  t.is_deductible
    ? T('Feriedager')
    : t.key === 'egenmelding'
      ? T('Egenmeldinger')
      : t.key === 'sykt_barn'
        ? T('Omsorgsdager')
        : '–';

const LAW: Record<string, string> = {
  ferie: '25 virkedager, +6 fra året man fyller 60 · arbeidsgiver varsler 2 mnd før (lov)',
  egenmelding: 'krever 2 mnd ansettelse · IA: inntil 8 d per gang, 24 per år',
  sykemelding: 'NAV fra dag 17',
  sykt_barn: '15 d med 3+ barn, dobbelt for aleneomsorg · barn under 12',
  foreldrepermisjon: '49 uker 100 % eller 61 uker 80 %',
  omsorgspermisjon: '2 uker (aml § 12-3)',
  svangerskapspermisjon: 'inntil 12 uker under svangerskapet (aml § 12-2)',
  svangerskapskontroll: 'etter behov (aml § 12-1)',
  ammefri: 'inntil 1 t per dag i barnets første år, betalt ved arbeidsdag på 7 t+',
  pleie_naerstaende: 'aml § 12-10',
  ferie_avtalefestet: 'den femte uken, bare under tariffavtale',
  pleie_livets_sluttfase: '60 d per nærstående',
  pleiepenger: 'etter vedtak fra NAV',
  religios_hoytid: 'arbeidsgiver kan kreve fridagene inndratt',
  militaertjeneste: 'lønn fra staten (aml § 12-12)',
  offentlige_verv: 'aml § 12-13',
  utdanning_lovfestet: '3 år i arbeid, 2 hos arbeidsgiveren (aml § 12-11)',
  avspasering: 'etter skriftlig avtale, i timer, fra timebanken (aml § 10-6 (12))',
};
export const lawNote = (t: ApiAbsenceType) => (t.key && LAW[t.key] ? T(LAW[t.key] as string) : '');

/** The type's limits, as the settings table and the drawer's rules list write them. */
export function limitsText(t: ApiAbsenceType): string[] {
  // ICU selects keep the Norwegian exactly as before: per is «12 mnd» or «år», the unit «arbeidsdager» or «d»
  const per = t.limit_period === 'rolling_year' ? 'rolling' : 'year';
  const unit = t.day_basis === 'arbeidsdager' ? 'work' : 'day';
  return [
    t.max_times
      ? T('{n} ganger per {per, select, rolling {12 mnd} other {år}}', { n: t.max_times, per })
      : '',
    t.max_days_per_use
      ? T('{n} {unit, select, work {arbeidsdager} other {d}} per gang', { n: t.max_days_per_use, unit })
      : '',
    t.max_days_per_period
      ? T(
          '{n} {unit, select, work {arbeidsdager} other {d}} per {per, select, rolling {12 mnd} other {år}}',
          {
            n: t.max_days_per_period,
            unit,
            per,
          },
        )
      : '',
    t.employer_paid_days
      ? T('{n} {unit, select, work {arbeidsdager} other {d}} arbeidsgiver', { n: t.employer_paid_days, unit })
      : '',
    t.notice_days ? T('{n} d søknadsfrist (bedriftens)', { n: t.notice_days }) : '',
  ].filter(Boolean);
}

export const A_MELDING = [
  'Permisjon med foreldrepenger',
  'Permisjon ved militærtjeneste',
  'Permittering',
  'Utdanningspermisjon (lovfestet)',
  'Utdanningspermisjon (ikke lovfestet)',
  'Andre lovfestede permisjoner',
  'Andre ikke-lovfestede permisjoner',
];

/**
 * A company's own type named like a built-in vacation type that does not draw on the balance.
 * The catalogue left such a type as the company made it (owner, 2026-09-18), so "Ferie" there
 * behaves differently from the built-in Ferie elsewhere; the screens say so.
 */
export const ownVacationWithoutDeduction = (t: ApiAbsenceType) =>
  !t.key && !t.is_deductible && ['ferie', 'avtalefestet ferie'].includes(t.name.trim().toLowerCase());
